import React, { useState, lazy, Suspense } from 'react';
import { 
  Box, 
  Container, 
  Tab, 
  TabList, 
  TabPanel, 
  TabPanels, 
  Tabs,
  Spinner,
  Center,
  VStack,
  Text
} from '@chakra-ui/react';

// Lazy load all the heavy components
const DailySchedule = lazy(() => import('../components/DailySchedule'));
const WeeklySchedule = lazy(() => import('../components/WeeklySchedule'));
const ClientManagement = lazy(() => import('../components/ClientManagement'));
const TherapistManagement = lazy(() => import('../components/TherapistManagement'));
const FinancialManagement = lazy(() => import('../components/FinancialManagement'));
const WaitlistManagement = lazy(() => import('../components/WaitlistManagement'));
const ReceptionistRequestsOthers = lazy(() => import('../components/ReceptionistRequestsOthers'));

const LoadingOverlay = () => (
  <Center height="500px" width="100%">
    <VStack spacing={4}>
      <Spinner 
        thickness="4px"
        speed="0.65s"
        emptyColor="gray.200"
        color="blue.500"
        size="xl"
      />
      <Text color="gray.500">Loading...</Text>
    </VStack>
  </Center>
);

// Wrapper component that controls visibility
const TabContentWrapper = ({ isActive, children }) => {
  return (
    <Box display={isActive ? 'block' : 'none'}>
      {children}
    </Box>
  );
};

const OwnerDashboard = () => {
  const [selectedTabIndex, setSelectedTabIndex] = useState(0);
  const [loadedTabs, setLoadedTabs] = useState(new Set([0])); // Track which tabs have been loaded

  const handleTabChange = (index) => {
    setSelectedTabIndex(index);
    // Mark this tab as loaded when user clicks on it
    setLoadedTabs(prev => new Set(prev).add(index));
  };

  // Only render components for tabs that have been visited
  const renderTabContent = (tabIndex, Component, staticKey, componentProps = {}) => {
    // Only load the component if this tab has been visited
    if (!loadedTabs.has(tabIndex)) {
      return null; // Don't render anything for unvisited tabs
    }

    return (
      <TabContentWrapper isActive={selectedTabIndex === tabIndex}>
        <Suspense fallback={<LoadingOverlay />}>
          <Component 
            key={staticKey} // Use static key that doesn't change
            LoadingComponent={LoadingOverlay}
            isVisible={selectedTabIndex === tabIndex} // Pass visibility prop
            {...componentProps}
          />
        </Suspense>
      </TabContentWrapper>
    );
  };

  return (
    <Container maxW="1200px" py={8}>
      <Tabs 
        isFitted 
        variant="enclosed" 
        index={selectedTabIndex} 
        onChange={handleTabChange}
      >
        <TabList mb="1em">
          <Tab>Daily Schedule</Tab>
          <Tab>Weekly Schedule</Tab>
          <Tab>Client Management</Tab>
          <Tab>Therapist Management</Tab>
          <Tab>Financial Management</Tab>
          <Tab>Waitlist</Tab>
          <Tab>Requests & Others</Tab>
        </TabList>

        <TabPanels>
          <TabPanel position="relative" minHeight="500px">
            {renderTabContent(0, DailySchedule, 'daily-schedule', { showSessionTrends: true })}
          </TabPanel>
          
          <TabPanel position="relative" minHeight="500px">
            {renderTabContent(1, WeeklySchedule, 'weekly-schedule')}
          </TabPanel>
          
          <TabPanel position="relative" minHeight="500px">
            {renderTabContent(2, ClientManagement, 'client-management')}
          </TabPanel>
          
          <TabPanel position="relative" minHeight="500px">
            {renderTabContent(3, TherapistManagement, 'therapist-management')}
          </TabPanel>
          
          <TabPanel position="relative" minHeight="500px">
            {renderTabContent(4, FinancialManagement, 'financial-management')}
          </TabPanel>

          <TabPanel position="relative" minHeight="500px">
            {renderTabContent(5, WaitlistManagement, 'waitlist-management')}
          </TabPanel>

          <TabPanel position="relative" minHeight="500px">
            {renderTabContent(6, ReceptionistRequestsOthers, 'requests-others')}
          </TabPanel>
        </TabPanels>
      </Tabs>
    </Container>
  );
};

export default OwnerDashboard;